import type {
  ReceiptDraftContentType,
  ReceiptDraftResponseDto,
  ReceiptScanLineItemDto,
  ReceiptScanResultDto,
  ReceiptScanSuggestionDto,
  ScanQuotaDto,
  ScanReceiptDraftDto,
} from '@splitfiesta/shared';
import {
  RECEIPT_SCAN_LOCK_BUDGET_MS,
  SCAN_ERROR_KEYS,
} from '@splitfiesta/shared';

import { EXPENSE_TAXONOMY_VERSION } from '../services/categoryPrediction/taxonomy';
import { isNetworkError } from '../utils/apiErrors';
import type { ReceiptFileKind } from './receiptFile';
import { fileKindOf } from './receiptFile';
import type { ScanFieldKey, ScanFormSnapshot, ScanMerge } from './scanState';
import {
  isDraftNotFoundError,
  isPdfUnreadableError,
  isProNotAvailableError,
  isScanQuotaError,
  mergeScan,
  scanRefusal,
} from './scanState';

/** What every draft row shows of its file. */
interface DraftFile {
  localUri: string;
  fileKind: ReceiptFileKind;
  fileName?: string;
  /** ISO time the file was picked, for the "Attached Oct 21" line. */
  pickedAt: string;
}

export type ScanRefusalKind = 'quota' | 'fairUse';

type FailureKey =
  'scan.unreadable' | 'errors:scan.unavailable' | 'errors:scan.pdfUnreadable';

export type ReceiptBlockState =
  | { kind: 'actions'; collapsed: boolean }
  | ({ kind: 'uploading' } & DraftFile)
  | ({ kind: 'reading'; draftId: string | null } & DraftFile)
  | ({
      kind: 'attached';
      draftId: string;
      readable: boolean;
      /** Set when the scan was refused for the month's quota or fair use. */
      refusal?: ScanRefusalKind;
    } & DraftFile)
  | ({ kind: 'read'; draftId: string; merge: ScanMerge | null } & DraftFile)
  | ({
      kind: 'failed';
      draftId: string | null;
      messageKey: FailureKey;
    } & DraftFile)
  /**
   * The edit form's stored receipt. `replacing` is the state of a new draft
   * shown in its place; removing that draft brings the stored one back.
   */
  | { kind: 'saved'; url: string; replacing: ReceiptBlockState | null };

export interface ScanAdapters {
  /** Platform image → JPEG with its long side ≤ RECEIPT_SCAN_MAX_PX; a PDF goes as is. */
  encode: (picked: unknown) => Promise<{
    localUri: string;
    bytes: number;
    body: unknown;
    contentType: ReceiptDraftContentType;
    fileName?: string;
  }>;
  createDraft: (
    groupId: string,
    contentLength: number,
    contentType: ReceiptDraftContentType
  ) => Promise<ReceiptDraftResponseDto>;
  put: (
    upload: ReceiptDraftResponseDto['upload'],
    body: unknown,
    signal: AbortSignal
  ) => Promise<void>;
  scan: (
    groupId: string,
    draftId: string,
    body: ScanReceiptDraftDto
  ) => Promise<ReceiptScanResultDto>;
  now: () => number;
  /** Defaults to RECEIPT_SCAN_LOCK_BUDGET_MS. */
  budgetMs?: number;
}

export interface ScanMachineCallbacks {
  snapshot: () => ScanFormSnapshot;
  groupCurrency: () => string;
  locale: () => string;
  localDate: () => string;
  onFill: (fills: ScanMerge['fills'], tags: ScanFieldKey[]) => void;
  onLines: (lines: ReceiptScanLineItemDto[], reconciled: boolean) => void;
  /** Write-back to the auth user; `fairUse` marks the session flag. */
  onQuota: (quota: ScanQuotaDto | null | undefined, fairUse: boolean) => void;
  onProNotAvailable: () => void;
  onUploadFailed: () => void;
  /** The draft expired server-side: the photo is gone and must be attached again. */
  onDraftExpired?: () => void;
  onSuccessFeedback: () => void;
}

export interface ReceiptScanMachine {
  getState(): ReceiptBlockState;
  subscribe(listener: (state: ReceiptBlockState) => void): () => void;
  locked(): boolean;
  scanInFlight(): boolean;
  draftId(): string | null;
  tags(): ReadonlySet<ScanFieldKey>;
  /** Line items the current draft's read returned; 0 before a read. */
  itemsRead(): number;
  scan(picked: unknown, groupId: string): Promise<void>;
  attach(picked: unknown, groupId: string): Promise<void>;
  readWithAi(groupId: string): Promise<void>;
  tryAgain(groupId: string): Promise<void>;
  /** "Type it myself" after a failed read: the uploaded file stays as a plain attachment. */
  keepAsAttachment(): void;
  replace(picked: unknown, groupId: string): Promise<void>;
  remove(): void;
  useRow(row: ScanMerge['useRows'][number]): void;
  keepMine(): void;
  /** Replays this draft's reading through the same merge as a read; no server call. */
  restore(): void;
  clearTag(field: ScanFieldKey): void;
  /** Discard during the lock, or unmount: a late completion is ignored. */
  abort(): void;
}

type DraftState = Exclude<ReceiptBlockState, { kind: 'saved' | 'actions' }>;

type Encoded = Awaited<ReturnType<ScanAdapters['encode']>>;

/** What a Try again needs to redo an upload the budget cut short. */
type UploadSource =
  { kind: 'picked'; picked: unknown } | { kind: 'encoded'; encoded: Encoded };

/** A picker result's own URI, so the row can show the photo before encode returns. */
function pickedUri(picked: unknown): string {
  if (typeof picked === 'string') return picked;
  const uri = (picked as { uri?: unknown } | null)?.uri;
  return typeof uri === 'string' ? uri : '';
}

/** A document pick or a web File carries its name. */
function pickedName(picked: unknown): string | undefined {
  const name = (picked as { name?: unknown } | null)?.name;
  return typeof name === 'string' && name !== '' ? name : undefined;
}

/** Until encode reports the content type, the pick is all there is to go by. */
function pickedKind(picked: unknown): ReceiptFileKind {
  if ((picked as { kind?: unknown } | null)?.kind === 'pdf') return 'pdf';
  return /\.pdf$/i.test(pickedName(picked) ?? '') ? 'pdf' : 'image';
}

function fromPick(picked: unknown, now: number): DraftFile {
  return {
    localUri: pickedUri(picked),
    fileKind: pickedKind(picked),
    fileName: pickedName(picked),
    pickedAt: new Date(now).toISOString(),
  };
}

/** A refused PDF fails the same way again; only a new file can help. */
export function isRetryableFailure(messageKey: FailureKey): boolean {
  return messageKey !== 'errors:scan.pdfUnreadable';
}

function failureKey(error: unknown): FailureKey {
  const key = (error as { translationKey?: unknown } | null)?.translationKey;
  if (key === SCAN_ERROR_KEYS.unavailable) return 'errors:scan.unavailable';
  if (isPdfUnreadableError(error)) return 'errors:scan.pdfUnreadable';
  return 'scan.unreadable';
}

/** Drops the panel when nothing is left to show in it. */
function trimMerge(merge: ScanMerge): ScanMerge | null {
  return merge.useRows.length > 0 || merge.checkCurrency ? merge : null;
}

export function createReceiptScanMachine(
  adapters: ScanAdapters,
  callbacks: ScanMachineCallbacks,
  initial?: { savedUrl: string | null; collapsed?: boolean }
): ReceiptScanMachine {
  const budgetMs = adapters.budgetMs ?? RECEIPT_SCAN_LOCK_BUDGET_MS;
  const listeners = new Set<(state: ReceiptBlockState) => void>();
  const tagSet = new Set<ScanFieldKey>();

  let savedUrl = initial?.savedUrl ?? null;
  let collapsed = initial?.collapsed ?? false;
  let inner: DraftState | null = null;
  let state: ReceiptBlockState = compose();
  let draft: string | null = null;
  let file: DraftFile = { localUri: '', fileKind: 'image', pickedAt: '' };
  let lockedFlag = false;
  let uploadingFlag = false;
  // Every flow captures the generation it started in; any later flow, a
  // remove, an abort or the budget bumps it, so a stale await drops its
  // result instead of overwriting the state the person now sees.
  let generation = 0;
  let controller: AbortController | null = null;
  let budgetTimer: ReturnType<typeof setTimeout> | null = null;
  let lockStartedAt = 0;
  // Set until the PUT resolves; a budget that runs out first keeps it so
  // Try again can upload the same photo instead of scanning a missing object.
  let pendingUpload: UploadSource | null = null;
  // The draft whose PUT the budget aborted; a PUT that still lands for it
  // makes that draft scannable again.
  let expiredDraft: string | null = null;
  // The reading this draft produced, kept so the person can come back to it
  // after editing; a new draft or a remove drops it with the draft.
  let lastSuggestion: ReceiptScanSuggestionDto | null = null;
  // Fields a scan filled and the person has not edited since. Remove clears
  // the visible tags but not this: a later scan may still overwrite a value
  // that only ever came from a receipt, and asks only about typed ones.
  const scanFilled = new Set<ScanFieldKey>();

  function compose(): ReceiptBlockState {
    if (savedUrl !== null)
      return { kind: 'saved', url: savedUrl, replacing: inner };
    return inner ?? { kind: 'actions', collapsed };
  }

  function emit() {
    state = compose();
    for (const listener of listeners) listener(state);
  }

  function show(next: DraftState | null) {
    inner = next;
    emit();
  }

  function stopWork() {
    controller?.abort();
    controller = null;
    if (budgetTimer !== null) clearTimeout(budgetTimer);
    budgetTimer = null;
    lockedFlag = false;
    uploadingFlag = false;
  }

  function backToBase() {
    lastSuggestion = null;
    draft = null;
    file = { localUri: '', fileKind: 'image', pickedAt: '' };
    pendingUpload = null;
    expiredDraft = null;
    show(null);
  }

  function isStale(gen: number) {
    return gen !== generation;
  }

  function startLock(gen: number) {
    lockedFlag = true;
    lockStartedAt = adapters.now();
    budgetTimer = setTimeout(() => expire(gen), budgetMs);
  }

  function expire(gen: number) {
    if (isStale(gen)) return;
    generation += 1;
    stopWork();
    if (pendingUpload !== null) {
      expiredDraft = draft;
      draft = null;
    }
    show({
      kind: 'failed',
      ...file,
      draftId: draft,
      messageKey: 'scan.unreadable',
    });
  }

  /**
   * JS timers stall while a phone app is suspended, but the clock does not,
   * so each step also checks the elapsed time against the budget.
   */
  function overBudget(gen: number): boolean {
    if (isStale(gen)) return true;
    if (lockedFlag && adapters.now() - lockStartedAt >= budgetMs) {
      expire(gen);
      return true;
    }
    return false;
  }

  /** Encode, draft and PUT; resolves false when the flow ended on its own. */
  async function upload(
    gen: number,
    source: UploadSource,
    groupId: string,
    onProgress: () => void
  ): Promise<boolean> {
    pendingUpload = source;
    expiredDraft = null;
    try {
      const encoded =
        source.kind === 'encoded'
          ? source.encoded
          : await adapters.encode(source.picked);
      if (isStale(gen)) return false;
      file = {
        localUri: encoded.localUri,
        fileKind: fileKindOf(encoded.contentType),
        fileName: encoded.fileName ?? file.fileName,
        pickedAt: file.pickedAt,
      };
      pendingUpload = { kind: 'encoded', encoded };
      if (overBudget(gen)) return false;
      onProgress();

      let created: ReceiptDraftResponseDto;
      try {
        created = await adapters.createDraft(
          groupId,
          encoded.bytes,
          encoded.contentType
        );
      } catch (error) {
        if (isStale(gen)) return false;
        if (isProNotAvailableError(error)) callbacks.onProNotAvailable();
        throw error;
      }
      if (overBudget(gen)) return false;
      draft = created.draftId;
      onProgress();

      controller = new AbortController();
      await adapters.put(created.upload, encoded.body, controller.signal);
      if (isStale(gen)) {
        adoptLateUpload(created.draftId);
        return false;
      }
      pendingUpload = null;
      return !overBudget(gen);
    } catch {
      if (isStale(gen)) return false;
      stopWork();
      backToBase();
      callbacks.onUploadFailed();
      return false;
    }
  }

  function adoptLateUpload(draftId: string) {
    if (
      expiredDraft !== draftId ||
      inner?.kind !== 'failed' ||
      inner.draftId !== null
    ) {
      return;
    }
    draft = draftId;
    expiredDraft = null;
    pendingUpload = null;
    show({ ...inner, draftId });
  }

  async function runScan(gen: number, groupId: string, draftId: string) {
    show({ kind: 'reading', ...file, draftId });
    let result: ReceiptScanResultDto;
    try {
      result = await adapters.scan(groupId, draftId, {
        taxonomyVersion: EXPENSE_TAXONOMY_VERSION,
        locale: callbacks.locale(),
        localDate: callbacks.localDate(),
      });
    } catch (error) {
      if (isStale(gen)) return;
      stopWork();
      if (isScanQuotaError(error)) {
        const refusal = scanRefusal(error.details);
        callbacks.onQuota(refusal.quota, refusal.fairUse);
        show({
          kind: 'attached',
          ...file,
          draftId,
          readable: false,
          refusal: refusal.fairUse ? 'fairUse' : 'quota',
        });
      } else if (isDraftNotFoundError(error)) {
        backToBase();
        callbacks.onDraftExpired?.();
      } else if (isNetworkError(error)) {
        // The upload landed, so the photo stays attached; the availability's
        // offline hint disables Read with AI until the connection is back.
        show({ kind: 'attached', ...file, draftId, readable: true });
      } else if (isProNotAvailableError(error)) {
        callbacks.onProNotAvailable();
        show({ kind: 'attached', ...file, draftId, readable: false });
      } else {
        show({
          kind: 'failed',
          ...file,
          draftId,
          messageKey: failureKey(error),
        });
      }
      return;
    }
    if (overBudget(gen)) return;
    stopWork();

    callbacks.onQuota(result.quota, false);
    lastSuggestion = result.suggestion;
    applySuggestion(result.suggestion);
    callbacks.onSuccessFeedback();
  }

  /** The read's fills, tags, lines and panel; shared by a scan and a replay. */
  function applySuggestion(suggestion: ReceiptScanSuggestionDto) {
    const snapshot = callbacks.snapshot();
    const merge = mergeScan(
      { ...snapshot, aiTagged: new Set([...snapshot.aiTagged, ...scanFilled]) },
      suggestion,
      callbacks.groupCurrency()
    );
    for (const tag of merge.tags) {
      tagSet.add(tag);
      scanFilled.add(tag);
    }
    if (merge.tags.length > 0) {
      if (merge.fills.amount !== undefined || merge.fills.title !== undefined) {
        collapsed = true;
      }
      callbacks.onFill(merge.fills, merge.tags);
    }
    if (suggestion.lineItems.length > 0) {
      callbacks.onLines(suggestion.lineItems, suggestion.lineItemsReconcile);
    }
    show({ kind: 'read', ...file, draftId: draft!, merge: trimMerge(merge) });
  }

  function begin(): number {
    lastSuggestion = null;
    stopWork();
    generation += 1;
    return generation;
  }

  async function scan(picked: unknown, groupId: string) {
    if (lockedFlag) return;
    const gen = begin();
    draft = null;
    file = fromPick(picked, adapters.now());
    startLock(gen);
    const showReading = () =>
      show({ kind: 'reading', ...file, draftId: draft });
    showReading();
    if (!(await upload(gen, { kind: 'picked', picked }, groupId, showReading)))
      return;
    await runScan(gen, groupId, draft!);
  }

  /** The budget ran out before the PUT landed: the same photo goes up again, then the scan. */
  async function reupload(groupId: string, source: UploadSource) {
    const gen = begin();
    draft = null;
    startLock(gen);
    const showReading = () =>
      show({ kind: 'reading', ...file, draftId: draft });
    showReading();
    if (!(await upload(gen, source, groupId, showReading))) return;
    await runScan(gen, groupId, draft!);
  }

  async function tryAgain(groupId: string) {
    if (lockedFlag || uploadingFlag) return;
    if (draft === null && pendingUpload !== null && inner?.kind === 'failed') {
      await reupload(groupId, pendingUpload);
      return;
    }
    await rescan(groupId);
  }

  async function attach(picked: unknown, groupId: string) {
    if (lockedFlag) return;
    const gen = begin();
    draft = null;
    file = fromPick(picked, adapters.now());
    uploadingFlag = true;
    const showUploading = () => show({ kind: 'uploading', ...file });
    showUploading();
    if (
      !(await upload(gen, { kind: 'picked', picked }, groupId, showUploading))
    )
      return;
    stopWork();
    show({ kind: 'attached', ...file, draftId: draft!, readable: true });
  }

  async function rescan(groupId: string) {
    if (lockedFlag || uploadingFlag || draft === null) return;
    const gen = begin();
    startLock(gen);
    await runScan(gen, groupId, draft);
  }

  function updateMerge(change: (merge: ScanMerge) => ScanMerge) {
    if (inner?.kind !== 'read' || inner.merge === null) return;
    show({ ...inner, merge: trimMerge(change(inner.merge)) });
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    locked: () => lockedFlag,
    scanInFlight: () => lockedFlag,
    draftId: () => draft,
    tags: () => new Set(tagSet),
    itemsRead: () => lastSuggestion?.lineItems.length ?? 0,
    scan,
    attach,
    readWithAi: rescan,
    tryAgain,
    keepAsAttachment() {
      // Without a draft the photo never reached the server: nothing to keep.
      if (inner?.kind !== 'failed' || draft === null) return;
      show({
        kind: 'attached',
        ...file,
        draftId: draft,
        readable: isRetryableFailure(inner.messageKey),
      });
    },
    // Replace attaches only: a new draft, no scan spent, tags kept.
    replace: attach,
    remove() {
      generation += 1;
      stopWork();
      tagSet.clear();
      if (inner === null && savedUrl !== null) {
        savedUrl = null;
        collapsed = true;
      }
      backToBase();
    },
    useRow(row) {
      tagSet.add(row.field);
      scanFilled.add(row.field);
      callbacks.onFill(row.apply, [row.field]);
      updateMerge(merge => ({
        ...merge,
        useRows: merge.useRows.filter(r => r.field !== row.field),
      }));
    },
    keepMine() {
      updateMerge(merge => ({ ...merge, useRows: [] }));
    },
    restore() {
      if (lockedFlag || uploadingFlag) return;
      if (inner?.kind !== 'read' || lastSuggestion === null) return;
      applySuggestion(lastSuggestion);
    },
    // The form calls this when the person edits the field, which also
    // settles that field's Use row.
    clearTag(field) {
      scanFilled.delete(field);
      const hadTag = tagSet.delete(field);
      const before = state;
      updateMerge(merge => ({
        ...merge,
        useRows: merge.useRows.filter(r => r.field !== field),
      }));
      if (hadTag && state === before) emit();
    },
    abort() {
      generation += 1;
      const inFlight = lockedFlag || uploadingFlag;
      stopWork();
      if (inFlight) backToBase();
    },
  };
}
